import React, { FC, useCallback, useEffect, useRef, useState } from 'react';
import _ from 'lodash';
import { Select, Spin } from 'antd';
import { useConnection } from '../../connection/Application';

/**
 * Async, searchable pickers for the bulk-email template merge-tag sources (employer + featured job).
 * Registered as form field types `selectRecruiter` / `selectJob` in CommonAdapters so they can be
 * used directly from a FormSchema. Both resolve a saved id's label on mount (so editing a template
 * shows the chosen name rather than the raw id).
 */

type Option = { value: string; label: string };
type Props = { value?: string; onChange?: (v?: string) => void; placeholder?: string };

const debouncedFetch = _.debounce(
  (fn: () => Promise<Option[]>, cb: (opts: Option[]) => void, done: () => void) => {
    fn()
      .then(cb)
      .catch(() => cb([]))
      .finally(done);
  },
  500,
);

const useAsyncSelect = (
  load: (params: { search?: string; id?: string }) => Promise<Option[]>,
  value?: string,
) => {
  const [options, setOptions] = useState<Option[]>([]);
  const [fetching, setFetching] = useState(false);
  const resolvedId = useRef<string>();

  // initial list
  useEffect(() => {
    load({})
      .then(setOptions)
      .catch(() => undefined);
  }, [load]);

  // resolve a saved value's label once (when not already present in options)
  useEffect(() => {
    if (!value || resolvedId.current === value) return;
    resolvedId.current = value;
    load({ id: value })
      .then((opts) =>
        setOptions((prev) => {
          const extra = opts.filter((o) => !prev.some((p) => p.value === o.value));
          return [...extra, ...prev];
        }),
      )
      .catch(() => undefined);
  }, [value, load]);

  const onSearch = useCallback(
    (search: string) => {
      setFetching(true);
      debouncedFetch(
        () => load({ search: search.trim() || undefined }),
        setOptions,
        () => setFetching(false),
      );
    },
    [load],
  );

  return { options, fetching, onSearch };
};

export const SelectRecruiter: FC<Props> = ({ value, onChange, placeholder }) => {
  const connection = useConnection();
  const load = useCallback(
    async (params: { search?: string; id?: string }) => {
      const res: any = await connection.get('admin/bulk-email/recruiters', params);
      return (res.recruiters ?? []).map((r: any) => ({
        value: r.recruiterId,
        label: r.email ? `${r.name} (${r.email})` : r.name,
      }));
    },
    [connection],
  );
  const { options, fetching, onSearch } = useAsyncSelect(load, value);

  return (
    <Select
      showSearch
      allowClear
      filterOption={false}
      value={value}
      onChange={onChange}
      onSearch={onSearch}
      placeholder={placeholder ?? 'Search an employer by name (optional)'}
      notFoundContent={fetching ? <Spin size="small" /> : null}
      style={{ width: '100%' }}
      options={options}
    />
  );
};

export const SelectJob: FC<Props> = ({ value, onChange, placeholder }) => {
  const connection = useConnection();
  const load = useCallback(
    async (params: { search?: string; id?: string }) => {
      const res: any = await connection.get('admin/bulk-email/job-search', params);
      return (res.jobs ?? []).map((j: any) => ({
        value: j.jobPostId,
        label: j.recruiterName ? `${j.jobTitle} — ${j.recruiterName}` : j.jobTitle,
      }));
    },
    [connection],
  );
  const { options, fetching, onSearch } = useAsyncSelect(load, value);

  return (
    <Select
      showSearch
      allowClear
      filterOption={false}
      value={value}
      onChange={onChange}
      onSearch={onSearch}
      placeholder={placeholder ?? 'Search a job by title (optional)'}
      notFoundContent={fetching ? <Spin size="small" /> : null}
      style={{ width: '100%' }}
      options={options}
    />
  );
};
